import React, { useRef, useCallback, useEffect, useState } from 'react';
import './HoloCard.css';

/**
 * HoloCard — wraps children with a holographic 3D tilt effect.
 * Mouse movement (or touch) rotates the card in 3D space with a rainbow
 * shimmer/glare overlay, like a holographic trading card.
 * Resets smoothly on mouse leave.
 */
export default function HoloCard({ children, maxTilt = 15, shimmerIntensity = 1 }) {
  const wrapperRef = useRef(null);
  const innerRef = useRef(null);
  const glareRef = useRef(null);
  const shimmerRef = useRef(null);
  const rafRef = useRef(null);
  const [idle, setIdle] = useState(true);

  // Lerp helper
  const lerp = (a, b, t) => a + (b - a) * t;

  // Current and target tilt values
  const state = useRef({ rx: 0, ry: 0, trx: 0, try_: 0, glareX: 50, glareY: 50 });

  const animate = useCallback(() => {
    const s = state.current;
    s.rx = lerp(s.rx, s.trx, 0.12);
    s.ry = lerp(s.ry, s.try_, 0.12);

    if (innerRef.current) {
      innerRef.current.style.transform = `rotateX(${s.rx}deg) rotateY(${s.ry}deg)`;
    }

    // Glare position follows mouse
    if (glareRef.current) {
      const angle = Math.atan2(s.ry, s.rx) * (180 / Math.PI);
      glareRef.current.style.transform = `rotate(${angle + 90}deg) scale(1.5)`;
    }

    // Shimmer stripe follows horizontal tilt
    if (shimmerRef.current) {
      const pct = ((s.ry + maxTilt) / (maxTilt * 2)) * 100;
      shimmerRef.current.style.backgroundPosition = `${pct}% 50%`;
      shimmerRef.current.style.backgroundSize = `300% 100%`;
    }

    rafRef.current = requestAnimationFrame(animate);
  }, [maxTilt]);

  const startAnimation = useCallback(() => {
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(animate);
    }
  }, [animate]);

  const stopAnimation = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    setIdle(false);
    const rect = wrapper.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2); // -1 to 1
    const dy = (e.clientY - cy) / (rect.height / 2); // -1 to 1

    state.current.trx = -dy * maxTilt;
    state.current.try_ = dx * maxTilt;
    startAnimation();
  }, [maxTilt, startAnimation]);

  const handleMouseLeave = useCallback(() => {
    // Reset to flat
    state.current.trx = 0;
    state.current.try_ = 0;
    setIdle(true);

    // Stop animation once settled
    const checkSettled = () => {
      const s = state.current;
      const dist = Math.abs(s.rx) + Math.abs(s.ry);
      if (dist < 0.05) {
        stopAnimation();
        if (innerRef.current) {
          innerRef.current.style.transform = `rotateX(0deg) rotateY(0deg)`;
        }
      } else {
        rafRef.current = requestAnimationFrame(checkSettled);
      }
    };

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    rafRef.current = requestAnimationFrame(checkSettled);
  }, [stopAnimation]);

  // Touch support — tilt based on touch position
  const handleTouchMove = useCallback((e) => {
    if (e.touches.length < 1) return;
    e.preventDefault();
    const touch = e.touches[0];
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    setIdle(false);
    const rect = wrapper.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (touch.clientX - cx) / (rect.width / 2);
    const dy = (touch.clientY - cy) / (rect.height / 2);

    state.current.trx = -dy * (maxTilt * 0.6);
    state.current.try_ = dx * (maxTilt * 0.6);
    startAnimation();
  }, [maxTilt, startAnimation]);

  const handleTouchEnd = useCallback(() => {
    handleMouseLeave();
  }, [handleMouseLeave]);

  // Gyroscope support (mobile)
  useEffect(() => {
    const handleOrientation = (e) => {
      // beta: front-to-back tilt (-180 to 180), gamma: left-right (-90 to 90)
      if (e.beta == null || e.gamma == null) return;
      const rx = Math.max(-maxTilt, Math.min(maxTilt, (e.beta - 45) * 0.5));
      const ry = Math.max(-maxTilt, Math.min(maxTilt, e.gamma * 0.5));
      state.current.trx = rx;
      state.current.try_ = ry;
      setIdle(false);
      startAnimation();
    };

    // Only attach on mobile devices
    if (window.matchMedia('(hover: none)').matches && window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', handleOrientation, { passive: true });
      return () => window.removeEventListener('deviceorientation', handleOrientation);
    }
  }, [maxTilt, startAnimation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopAnimation();
  }, [stopAnimation]);

  // Respect prefers-reduced-motion
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    return <>{children}</>;
  }

  return (
    <div
      className={`holo-card-wrapper${idle ? ' holo-idle' : ''}`}
      ref={wrapperRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="holo-card-inner" ref={innerRef}>
        {children}
        {/* Rainbow glare overlay */}
        <div
          className="holo-glare"
          ref={glareRef}
          style={{ opacity: idle ? 0 : shimmerIntensity * 0.85 }}
        />
        {/* Shimmer stripe */}
        <div
          className="holo-shimmer"
          ref={shimmerRef}
          style={{ opacity: idle ? 0 : shimmerIntensity * 0.9 }}
        />
      </div>
      <div className="holo-hint">✨ hover to tilt</div>
    </div>
  );
}
